import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Collapse, Divider } from '@mui/material';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import PropTypes from 'prop-types';
import Switch from '@mui/material/Switch';
import Badge from '@mui/material/Badge';

import { withTranslation } from 'react-i18next';
import {
    DeviceTypeTypeahead,
    LeasingContractMultiTypeahead,
    UsersMultiTypeahead
} from '../Typeahead';
import { fetchDevices } from '../../../actions';
import PersistentStorage from '../../../services/storage';
import {
    checkSwitchDisableStatus,
    dateFormatValidation,
    mapDeviceFiltersStateToQuery,
    badgesFilter,
    DEVICE_FILTER_FIELD_INIT,
    LOCATION_STATES,
    LOCATION_STATES_ID,
    getMinDate,
    getNextYearDateFormatWithoutTime,
    getNextTenCalendarYears, 
    getPreviousTenCalendarYears,
    COLORS
} from '../../../utils';
import { updateFilters, applyFilters, resetApplyFilters } from '../../../actions/devicesFilters';
import LocationTreeModeTypeahead from '../Typeahead/LocationTreeModeTypeahead';
import DeviceManufacturerTypeHead from '../Typeahead/DeviceManufacturerTypeHead';
import PositionInCompanyTypeahead from '../Typeahead/PositionInCompanyTypeahead';
import ProjectsInCompanyTypeahead from '../Typeahead/ProjectsInCompanyTypeahead';
import PrimarySkillTypeahead from '../Typeahead/PrimarySkillTypeahead';
import RoleOnProjectTypeahead from '../Typeahead/RoleOnProjectTypeahead';
import DismissedEmployees from '../DismissedEmployees';
import DeviceIdMultiInput from '../Typeahead/DeviceIdMultiInput';
import DateRangePicker from '../DateRangePicker';
import GeneralProgressBar from '../GeneralProgressBar';
import OSTypeahead from '../Typeahead/OSTypeahead';
import ProcessorNameTypeahead from '../Typeahead/ProcessorNameTypeahead';
import { ProcessorCores } from '../Typeahead/ProcessorCores';
import { DiskSize } from '../Typeahead/DiskSize';
import { RamSize } from '../Typeahead/RamSize';
import { RamSlot } from '../Typeahead/RamSlot';

const styles = theme => ({
    button: {
        margin: theme.spacing(0.5),
        marginTop: theme.spacing(2),
        flexGrow: 1
    },
    filterForm: {
        padding: theme.spacing(1)
    },
    controlButtons: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-around'
    },
    menu: {
        width: '90%',
        padding: '0, 5px, 0, 5px'
    },
    totalWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '5px',
        width: '90%'
    },
    totalBadgeFilter: {
        zIndex: '0',
        '& .MuiBadge-badge': {
            borderRadius: '50%',
            width: '25px',
            height: '25px',
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main
        }
    },
    wrapperRow: {
        width: '100%',
        marginTop: '10px',
        backgroundColor: theme.palette.action.hover,
        boxShadow: `0px 0px 5px 3px ${theme.palette.action.hover}`
    },
    badgeFilter: {
        zIndex: '0',
        top: '-19px',
        right: '12px',
        transformOrigin: '100% 0%',
    },
    marginStates: {
        marginLeft: '15px'
    },
    changeFont: { fontFamily: 'Helvetica, Arial, sans-serif' },
    specificationWrapper: {
        margin: '15px 0',
        padding: '5px 10px',
        boxShadow: `0 0 10px 0.5px ${COLORS.disabled.color}`,
        borderRadius: '10px',
        minHeight: '100px',
    },
    SpecificationBox: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    formControlWrapper: {
        width: '45%', margin: 0
    },
    inputLabelSpecification: {
        fontSize: '1.4rem', marginTop: '5px'
    },

});

const minDate = getMinDate('01/01/1996');
const maxDate = getNextYearDateFormatWithoutTime();

const minDateTen = getPreviousTenCalendarYears();
const maxDateTen = getNextTenCalendarYears();

class DeviceFilter extends Component {
    state = {
        assignmentDateFocusedInput: null,
        deliveryDateFocusedInput: null,
        deviceIdValue: '',
        errors: {
            assignmentDateFrom: null,
            assignmentDateTo: null,
            deliveryDateFrom: null,
            deliveryDateTo: null
        },
        filterMenuState: {
            deviceMenu: false,
            deviceSpecificationMenu: false,
            userMenu: false,
            locationMenu: false,
            dateMenu: false,
            conditionMenu: false,
            leasingMenu: false,
        },
    };

    componentDidMount() {
        const { i18n } = this.props;
        i18n.loadNamespaces(['devices']);
        dateFormatValidation('assignmentDateFrom', 'assignmentDateTo');
        dateFormatValidation('deliveryDateFrom', 'deliveryDateTo');
        const filterMenuGroupCondition = PersistentStorage.getItem('deviceFilterMenuState');
        if (filterMenuGroupCondition) {
            this.setState(state => ({
                ...state,
                filterMenuState: filterMenuGroupCondition,
            }));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.filterMenuState !== this.state.filterMenuState) {
            PersistentStorage.setItem('deviceFilterMenuState', this.state.filterMenuState);
        }
    }

    handleResetClick = async () => {
        await this.props.resetApplyFilters();

        const filters = mapDeviceFiltersStateToQuery(this.props.devicesFilters);

        this.props.fetchDevices(filters);
    };

    handleChangeSearchData = e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [e.target.name]: e.target.value
        });
    };

    handleClickApply = e => {
        e.preventDefault();
        const { assignmentDateFrom, assignmentDateTo, deliveryDateFrom, deliveryDateTo } = this.state.errors;

        const isError = assignmentDateFrom || assignmentDateTo || deliveryDateFrom || deliveryDateTo;

        if (!isError) {
            const filters = mapDeviceFiltersStateToQuery(this.props.devicesFilters);
            this.props.applyFilters(this.props.devicesFilters);
            this.props.fetchDevices(filters);
        }
    };

    handleChangeDate = name => date => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [name]: date,
        });
    };

    handleChangeCondition = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            condition: {
                ...this.props.devicesFilters.condition,
                [name]: e.target.checked
            }
        });
    };

    handleChangeIsInEndpointCentral = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [name]: e.target.checked,
            oSTypes: [],
            processorNameTypes: [],
            fromCoreCountTypes: [],
            toCoreCountTypes: [],
            fromDiskSizeTypes: [],
            toDiskSizeTypes: [],
            fromRamSizeTypes: [],
            toRamSizeTypes: [],
            fromRamSlotTypes: [],
            toRamSlotTypes: [],
        });
    };

    handleChangeStatusOffices = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [name]: e.target.checked ? LOCATION_STATES.all : LOCATION_STATES.onlyActive
        });
    };

    handleChangeReserved = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [name]: e.target.checked
        });
    };

    handleChangeState = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            state: {
                ...this.props.devicesFilters.state,
                [name]: e.target.checked
            }
        });
    };

    handleChangePrimaryDevice = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            primary: {
                ...this.props.devicesFilters.primary,
                [name]: e.target.checked
            },
        });
    };

    handleChangeInfrastructureUnit = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            infrastructureUnit: {
                ...this.props.devicesFilters.infrastructureUnit,
                [name]: e.target.checked
            },
        });
    };

    handleChangeLeasingContract = name => e => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            leasingContract: {
                ...this.props.devicesFilters.leasingContract,
                [name]: e.target.checked
            },
        });
    };

    handleChangeMultiSuggestion = name => suggestions => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [name]: suggestions
        });
    };

    handleChangeSuggestion = name => suggestions => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            [name]: suggestions ? [suggestions] : []
        });
    };

    handleChangeGroupFields = name => {
        this.setState(state => ({ ...state,
            filterMenuState: { ...state.filterMenuState, [name]: !this.state.filterMenuState[name] } }));
    }

    getNumberOfSelectedFilters = (filtersAll, initObj) => Object.entries(filtersAll).reduce((acc, arr) => {

        Object.keys(initObj).forEach(el => badgesFilter(initObj[el], arr, acc, el));
        return acc;

    }, { deviceFilter: 0,
        leasedDeviceFilter: 0,
        deviceSpecificationFilter: 0,
        userFilter: 0,
        locationFilter: 0,
        dateFilter: 0,
        conditionFilter: 0,
        total: 0 });

    updateFilterDismissedEmployees = e => {
        const value = e.target.checked;
        this.props.updateFilters({
            ...this.props.devicesFilters,
            ...this.props.devicesFilters.state,
            isDismissedEmployees: value || null,
            users: [],
            positionsInCompany: [],
            roleOnProject: [],
            projectsInCompany: [],
            primarySkill: [],
        });
    }

    handleChangeDeviceIdValue = value => {
        this.setState({ deviceIdValue: value });
    }

    handleAddIdMultiInput = value => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            ids: [...this.props.devicesFilters.ids, { value }],
        });
        this.setState({ deviceIdValue: '' });
    }

    handleDeleteItemIdMultiInput = values => {
        this.props.updateFilters({
            ...this.props.devicesFilters,
            ids: values,
        });
    }

    handleErrorDate = (error, name) => {
        this.setState(state => ({
            ...state,
            errors: {
                ...state.errors,
                [name]: error
            }
        }));
    }

    render() {
        const { classes, devicesFilters, offices, whereabouts, t } = this.props;
        const { isDismissedEmployees, officeState, resourceOwnerState } = devicesFilters;
        const { filterMenuState: {
            deviceMenu,
            deviceSpecificationMenu,
            userMenu,
            locationMenu,
            dateMenu,
            conditionMenu,
            leasingMenu
        },
        deviceIdValue, errors } = this.state;
        const { condition: deviceConditions,
            state: deviceStates,
            primary: primaryDevice,
            isReserved: reserved,
            infrastructureUnit,
            leasingContract,
            isInEndpointCentral } = devicesFilters;
        const { working, nonWorking, workingConditionally } = deviceConditions;
        const { inUse, onStock, underRepair, ordered, inTransit, inOffice } = deviceStates;
        const { yes: yesIU, no: noIU } = infrastructureUnit;
        const { yes: yesLC, no: noLC } = leasingContract;
        const { yes, no } = primaryDevice;
        const {
            deviceFilter,
            leasedDeviceFilter,
            userFilter,
            locationFilter,
            deviceSpecificationFilter,
            dateFilter,
            conditionFilter,
        } = this.getNumberOfSelectedFilters(this.props.devicesFilters, DEVICE_FILTER_FIELD_INIT);

        const { total } = this.getNumberOfSelectedFilters(this.props.appliedDeviceFilters, DEVICE_FILTER_FIELD_INIT);

        const isOfficeState = officeState === LOCATION_STATES_ID.all.id;
        const isResourceOwnerState = resourceOwnerState === LOCATION_STATES_ID.all.id;
        return (offices && whereabouts) ? (
            <form
                className={classNames('filter', classes.filterForm)}
                onSubmit={this.handleClickApply}
            >

                <div className={classNames(classes.totalWrapper)}>
                    <span>
                        {t('devices:totalFilterFieldsSelected')}
                        :
                    </span>
                    {!!total && (
                        <Badge
                            className={classNames(classes.totalBadgeFilter)}
                            badgeContent={total}
                            overlap="rectangular"
                            invisible={!total}
                        />
                    )}
                </div>

                <FormControl fullWidth>
                    <InputLabel htmlFor="searchData">
                        {t('search')}
                    </InputLabel>
                    <Input
                        className={classes.changeFont}
                        id="searchData"
                        name="searchData"
                        value={devicesFilters.searchData}
                        onChange={this.handleChangeSearchData}
                    />
                </FormControl>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={deviceMenu}
                                onChange={() => this.handleChangeGroupFields('deviceMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('device')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={deviceFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(deviceFilter && !deviceMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={deviceMenu}>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <DeviceIdMultiInput
                            value={deviceIdValue}
                            onChangeValue={this.handleChangeDeviceIdValue}
                            values={devicesFilters.ids}
                            onChangeMultiInput={this.handleAddIdMultiInput}
                            onDeleteValue={this.handleDeleteItemIdMultiInput}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <DeviceTypeTypeahead
                            animatedPlaceholder
                            isMulti
                            value={devicesFilters.deviceTypes}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('deviceTypes')}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <DeviceManufacturerTypeHead
                            animatedPlaceholder
                            labelId="deviceManufacturer"
                            margin="none"
                            name="deviceManufacturer"
                            value={devicesFilters.deviceManufacturers}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('deviceManufacturers')}
                            t={t}
                        />
                    </FormControl>
                    <FormControl
                        fullWidth
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('infrastructureUnit')}
                        </FormLabel>
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={yesIU}
                                    onChange={this.handleChangeInfrastructureUnit('yes')}
                                    disabled={checkSwitchDisableStatus(yesIU, infrastructureUnit)}
                                />
                            )}
                            label={t('yes')}
                        />

                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={noIU}
                                    onChange={this.handleChangeInfrastructureUnit('no')}
                                    disabled={checkSwitchDisableStatus(noIU, infrastructureUnit)}
                                />
                            )}
                            label={t('no')}
                        />
                    </FormControl>
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={userMenu}
                                onChange={() => this.handleChangeGroupFields('userMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('userProfile')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={userFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(userFilter && !userMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={userMenu}>
                    <DismissedEmployees
                        setDismissedEmployees={this.updateFilterDismissedEmployees}
                        dismissedEmployees={isDismissedEmployees}
                        classes={classes}
                        t={t}
                    />
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <UsersMultiTypeahead
                            animatedPlaceholder
                            margin="none"
                            value={devicesFilters.users}
                            labelId="user"
                            onChangeSuggestion={this.handleChangeMultiSuggestion('users')}
                            isDismissedEmployees={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <PositionInCompanyTypeahead
                            animatedPlaceholder
                            labelId="positionInCompany"
                            margin="none"
                            name="positionInCompany"
                            value={devicesFilters.positionsInCompany}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('positionsInCompany')}
                            disabled={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <RoleOnProjectTypeahead
                            animatedPlaceholder
                            labelId="roleOnProject"
                            margin="none"
                            name="roleOnProject"
                            value={devicesFilters.roleOnProject}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('roleOnProject')}
                            disabled={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <ProjectsInCompanyTypeahead
                            animatedPlaceholder
                            labelId="projectsInCompany"
                            margin="none"
                            name="projectsInCompany"
                            value={devicesFilters.projectsInCompany}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('projectsInCompany')}
                            disabled={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <PrimarySkillTypeahead
                            animatedPlaceholder
                            labelId="primarySkill"
                            margin="none"
                            name="primarySkill"
                            value={devicesFilters.primarySkill}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('primarySkill')}
                            disabled={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <LocationTreeModeTypeahead
                            data={[whereabouts.items[0]]}
                            placeholder="whereabouts"
                            locationId={devicesFilters.whereabouts.map(location => location.value)}
                            setLocation={value =>
                                this.handleChangeMultiSuggestion('whereabouts')(value.map(item => ({ value: item })))}
                            hierarchical
                            multi
                            descendants
                        />
                    </FormControl>
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={locationMenu}
                                onChange={() => this.handleChangeGroupFields('locationMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('office')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={locationFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(locationFilter && !locationMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={locationMenu}>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={isOfficeState}
                                    onChange={this.handleChangeStatusOffices('officeState')}
                                />
                            )}
                            label={t('active/All')}
                        />
                        <LocationTreeModeTypeahead
                            data={[isOfficeState ? offices.all : offices.onlyActive]}
                            placeholder="office"
                            locationId={devicesFilters.offices.map(location => location.value)}
                            strict={devicesFilters.strict}
                            setLocation={value =>
                                this.handleChangeMultiSuggestion('offices')(value.map(item => ({ value: item })))}
                            setStrict={value =>
                                this.handleChangeMultiSuggestion('strict')(value)}
                            hierarchical
                            multi
                            descendants
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={isResourceOwnerState}
                                    onChange={this.handleChangeStatusOffices('resourceOwnerState')}
                                />
                            )}
                            label={t('active/All')}
                        />
                        <LocationTreeModeTypeahead
                            data={[isResourceOwnerState ? offices.all : offices.onlyActive]}
                            placeholder="resourceOwner"
                            locationId={devicesFilters.resourceOwner.map(location => location.value)}
                            strict={devicesFilters.resourceOwnerStrict}
                            setLocation={value =>
                                this.handleChangeMultiSuggestion('resourceOwner')(value.map(item => ({ value: item })))}
                            setStrict={value =>
                                this.handleChangeMultiSuggestion('resourceOwnerStrict')(value)}
                            hierarchical
                            multi
                            descendants
                        />
                    </FormControl>

                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={dateMenu}
                                onChange={() => this.handleChangeGroupFields('dateMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('date')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={dateFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(dateFilter && !dateMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={dateMenu}>
                    <DateRangePicker
                        isColumn
                        startName="assignmentDateFrom"
                        endName="assignmentDateTo"
                        title="assignmentDate"
                        minDate={minDate}
                        maxDate={maxDate}
                        value={{
                            startDate: devicesFilters.assignmentDateFrom,
                            endDate: devicesFilters.assignmentDateTo
                        }}
                        handleChangeStartDate={this.handleChangeDate}
                        handleChangeEndDate={this.handleChangeDate}
                        handleError={this.handleErrorDate}
                        errors={errors}
                        t={t}
                    />

                    <DateRangePicker
                        isColumn
                        startName="deliveryDateFrom"
                        endName="deliveryDateTo"
                        title="deliveryDate"
                        minDate={minDate}
                        maxDate={maxDate}
                        value={{
                            startDate: devicesFilters.deliveryDateFrom,
                            endDate: devicesFilters.deliveryDateTo
                        }}
                        handleChangeStartDate={this.handleChangeDate}
                        handleChangeEndDate={this.handleChangeDate}
                        handleError={this.handleErrorDate}
                        errors={errors}
                        t={t}
                    />
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={conditionMenu}
                                onChange={() => this.handleChangeGroupFields('conditionMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('deviceState')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={conditionFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(conditionFilter && !conditionMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={conditionMenu}>
                    <FormControl
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('condition')}
                        </FormLabel>
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(working, deviceConditions)}
                                    checked={working}
                                    onChange={this.handleChangeCondition('working')}
                                />
                            )}
                            label={t('working')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(nonWorking, deviceConditions)}
                                    checked={nonWorking}
                                    onChange={this.handleChangeCondition('nonWorking')}
                                />
                            )}
                            label={t('nonWorking')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(workingConditionally, deviceConditions)}
                                    checked={workingConditionally}
                                    onChange={this.handleChangeCondition('workingConditionally')}
                                />
                            )}
                            label={t('workingConditionally')}
                        />
                    </FormControl>

                    <FormControl
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('state')}
                        </FormLabel>
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(inUse, deviceStates)}
                                    checked={inUse}
                                    onChange={this.handleChangeState('inUse')}
                                />
                            )}
                            label={t('inUse')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(onStock, deviceStates)}
                                    checked={onStock}
                                    onChange={this.handleChangeState('onStock')}
                                />
                            )}
                            label={t('onStock')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(inTransit, deviceStates)}
                                    checked={inTransit}
                                    onChange={this.handleChangeState('inTransit')}
                                />
                            )}
                            label={t('inTransit')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(inOffice, deviceStates)}
                                    checked={inOffice}
                                    onChange={this.handleChangeState('inOffice')}
                                />
                            )}
                            label={t('inOffice')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(underRepair, deviceStates)}
                                    checked={underRepair}
                                    onChange={this.handleChangeState('underRepair')}
                                />
                            )}
                            label={t('underRepair')}
                        />
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    disabled={checkSwitchDisableStatus(ordered, deviceStates)}
                                    checked={ordered}
                                    onChange={this.handleChangeState('ordered')}
                                />
                            )}
                            label={t('ordered')}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('reservation')}
                        </FormLabel>
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={reserved}
                                    onChange={this.handleChangeReserved('isReserved')}
                                />
                            )}
                            label={t('reservedOnly')}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('primaryDevice')}
                        </FormLabel>
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={yes}
                                    onChange={this.handleChangePrimaryDevice('yes')}
                                    disabled={checkSwitchDisableStatus(yes, primaryDevice)}
                                />
                            )}
                            label={t('yes')}
                        />

                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={no}
                                    onChange={this.handleChangePrimaryDevice('no')}
                                    disabled={checkSwitchDisableStatus(no, primaryDevice)}
                                />
                            )}
                            label={t('no')}
                        />
                    </FormControl>
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={deviceSpecificationMenu}
                                onChange={() => this.handleChangeGroupFields('deviceSpecificationMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('deviceSpecification')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={deviceSpecificationFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(deviceSpecificationFilter && !deviceSpecificationMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={deviceSpecificationMenu}>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={isInEndpointCentral}
                                    onChange={this.handleChangeIsInEndpointCentral('isInEndpointCentral')}
                                />
                            )}
                            label={t('displayOnlyECDevices')}
                        />
                    </FormControl>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <OSTypeahead
                            animatedPlaceholder
                            margin="none"
                            value={devicesFilters.oSTypes}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('oSTypes')}
                            disabled={!isInEndpointCentral}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <ProcessorNameTypeahead
                            animatedPlaceholder
                            margin="none"
                            value={devicesFilters.processorNameTypes}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('processorNameTypes')}
                            disabled={!isInEndpointCentral}
                            t={t}
                        />
                    </FormControl>

                    <ProcessorCores
                        fromCore={devicesFilters.fromCoreCountTypes}
                        toCore={devicesFilters.toCoreCountTypes}
                        onChange={this.handleChangeSuggestion}
                        disabled={!isInEndpointCentral}
                        classes={classes}
                        t={t}
                    />

                    <DiskSize
                        fromDiskSize={devicesFilters.fromDiskSizeTypes}
                        toDiskSize={devicesFilters.toDiskSizeTypes}
                        onChange={this.handleChangeSuggestion}
                        disabled={!isInEndpointCentral}
                        classes={classes}
                        t={t}
                    />

                    <RamSize
                        fromRamSize={devicesFilters.fromRamSizeTypes}
                        toRamSize={devicesFilters.toRamSizeTypes}
                        onChange={this.handleChangeSuggestion}
                        disabled={!isInEndpointCentral}
                        classes={classes}
                        t={t}
                    />

                    <RamSlot
                        fromRamSlot={devicesFilters.fromRamSlotTypes}
                        toRamSlot={devicesFilters.toRamSlotTypes}
                        onChange={this.handleChangeSuggestion}
                        disabled={!isInEndpointCentral}
                        classes={classes}
                        t={t}
                    />

                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={leasingMenu}
                                onChange={() => this.handleChangeGroupFields('leasingMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('leasedDevices')}
                    />
                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={leasedDeviceFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(leasedDeviceFilter && !leasingMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={leasingMenu}>
                    <FormControl
                        fullWidth
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('leasingContract')}
                        </FormLabel>
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={yesLC}
                                    onChange={this.handleChangeLeasingContract('yes')}
                                    disabled={checkSwitchDisableStatus(yesLC, leasingContract)}
                                />
                            )}
                            label={t('yes')}
                        />

                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={noLC}
                                    onChange={this.handleChangeLeasingContract('no')}
                                    disabled={checkSwitchDisableStatus(noLC, leasingContract)}
                                />
                            )}
                            label={t('no')}
                        />
                    </FormControl>
                    <LeasingContractMultiTypeahead
                        value={devicesFilters.LeasingContractIds}
                        onChangeSuggestion={this.handleChangeMultiSuggestion('LeasingContractIds')}
                        t={t}
                    />
                    <DateRangePicker
                        isColumn
                        startName="LeasingContractEndDateFrom"
                        endName="LeasingContractEndDateTo"
                        title="leasingContractEndDate"
                        minDate={minDateTen}
                        maxDate={maxDateTen}
                        value={{
                            startDate: devicesFilters.LeasingContractEndDateFrom,
                            endDate: devicesFilters.LeasingContractEndDateTo
                        }}
                        handleChangeStartDate={this.handleChangeDate}
                        handleChangeEndDate={this.handleChangeDate}
                        handleError={this.handleErrorDate}
                        errors={errors}
                        t={t}
                    />
                </Collapse>

                <div className={classes.controlButtons}>
                    <Button
                        variant="contained"
                        className={classes.button}
                        onClick={this.handleResetClick}
                    >
                        {t('reset')}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.button}
                        color="secondary"
                        type="submit"
                    >
                        {t('apply')}
                    </Button>
                </div>
            </form>
        ) : (<GeneralProgressBar />);
    }
}

DeviceFilter.propTypes = {
    fetchDevices: PropTypes.func,
    updateFilters: PropTypes.func,
    applyFilters: PropTypes.func,
    resetApplyFilters: PropTypes.func,
    classes: PropTypes.shape({
        filterForm: PropTypes.string,
        controlButtons: PropTypes.string,
        button: PropTypes.string,
    }),
    devicesFilters: PropTypes.shape({
        infrastructureUnit: PropTypes.shape({
            yes: PropTypes.bool,
            no: PropTypes.bool,
        }),
        primary: PropTypes.shape({
            yes: PropTypes.bool,
            no: PropTypes.bool,
        }),
        condition: PropTypes.shape({
            working: PropTypes.bool,
            nonWorking: PropTypes.bool,
            workingConditionally: PropTypes.bool,
        }),
        state: PropTypes.shape({
            inUse: PropTypes.bool,
            onStock: PropTypes.bool,
            underRepair: PropTypes.bool,
            ordered: PropTypes.bool,
            inTransit: PropTypes.bool,
            inOffice: PropTypes.bool,
        }),
        offices: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.number,
            }),
        ),
        strict: PropTypes.bool,
        officeState: PropTypes.number,
        resourceOwnerState: PropTypes.number,
        isDismissedEmployees: PropTypes.bool,
        deviceTypes: PropTypes.array,
        searchData: PropTypes.string,
        users: PropTypes.array,
        assignmentDateFrom: PropTypes.object,
        assignmentDateTo: PropTypes.object,
        deliveryDateFrom: PropTypes.object,
        deliveryDateTo: PropTypes.object,
        LeasingContractEndDateFrom: PropTypes.object,
        LeasingContractEndDateTo: PropTypes.object,
        LeasingContractIds: PropTypes.array,
    }),
    offices: PropTypes.object,
};

const mapStateToProps = ({ devicesFilters, infrastructure }) => ({
    devicesFilters: devicesFilters.currentFilters,
    appliedDeviceFilters: devicesFilters.appliedFilters,
    offices: infrastructure.locations[0],
    whereabouts: infrastructure.whereabouts[0]
});

export default compose(
    withTranslation('devices'),
    connect(mapStateToProps, { fetchDevices, updateFilters, applyFilters, resetApplyFilters }),
    withStyles(styles)
)(DeviceFilter);
